import { Amplify } from 'aws-amplify'
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth'

/**
 * Amplify configuration and token access.
 *
 * **Ported verbatim from aivy-coach at `b0da0e2` (2026-08-20)**, which is the
 * commit Sprint 1.8's spec was written against and the last one where this file
 * is 39 lines. At AIVY's HEAD it is 314, because SPRINT-BIO-1 moved Amplify's
 * tokens into the iOS Keychain: it now imports `./appLock`, `./nativeShell`,
 * `./authMutationLane` and thirteen symbols from a 1702-line Capacitor adapter.
 * Porting that would mean porting Capacitor, which §Non-goals forbids.
 *
 * The only additions are `getCurrentAuthUser` — AuthContext needs it and the
 * later AIVY split it out — and the doc comments.
 *
 * Token storage stays inside Amplify (§3.6). There is no custom refresh timer
 * and no 401-retry queue (§3.7): a 401 emits an event, the context signs out,
 * the router bounces to sign-in.
 */

let configured = false

export function authDisabled(): boolean {
  return import.meta.env.VITE_AUTH_DISABLED === 'true'
}

export function configureAmplifyAuth() {
  if (configured) return
  if (authDisabled()) {
    configured = true
    return
  }

  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID
  const userPoolClientId = import.meta.env.VITE_COGNITO_APP_CLIENT_ID

  if (userPoolId && userPoolClientId) {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId,
          userPoolClientId,
        },
      },
    })
  }

  configured = true
}

/** Resolves only when Amplify holds a session. Rejects otherwise — that is the signal. */
export async function getCurrentAuthUser() {
  configureAmplifyAuth()
  return getCurrentUser()
}

/**
 * The ACCESS token, deliberately — never the id token.
 *
 * `brainspot-backend/README.md:620`: "A Cognito access token carries no `email`
 * claim — only the ID token does, and those are refused." The backend fetches
 * attributes once per first sign-in instead.
 */
export async function getAccessToken(): Promise<string | null> {
  if (authDisabled()) return null
  configureAmplifyAuth()
  const session = await fetchAuthSession()
  return session.tokens?.accessToken?.toString() ?? null
}

/** Test seam: `configured` is module state and would otherwise leak between cases. */
export function __resetAmplifyConfigForTests() {
  configured = false
}
